<template>
  <div v-if="isReady">
    <!-- Inline video player: video visible with controls overlaid -->
    <div
      v-if="mediaFile"
      class="relative w-full bg-black overflow-hidden rounded-lg"
      :class="videoWidth && videoHeight ? '' : 'aspect-video'"
      :style="videoWidth && videoHeight ? { aspectRatio: `${videoWidth} / ${videoHeight}` } : {}"
      @mousemove="onControlsActivity"
      @touchstart.passive="onControlsActivity"
    >
      <video
        ref="videoRef"
        class="w-full h-full object-contain"
        preload="metadata"
        playsinline
        @play="onVideoPlayEvent"
        @pause="onVideoPauseEvent"
        @timeupdate="handleTimeUpdate"
        @seeking="handleSeeking"
        @seeked="handleSeeked"
        @loadedmetadata="onLoadedMetadata"
        @ended="onVideoEndedEvent"
      >
        <source :src="mediaFile" :type="mimeType" />
      </video>

      <!-- Poster overlay: shown before first play, hides once video is playing -->
      <img
        v-if="!hasStartedPlaying && computedPoster"
        :src="computedPoster"
        class="absolute inset-0 w-full h-full object-cover z-10"
        alt=""
      />

      <!-- Controls overlay (above the poster) -->
      <div
        class="absolute inset-0 flex flex-col z-20"
        :class="controlsOverlayClasses"
      >
        <!-- Top bar: fullscreen button -->
        <div class="flex justify-end p-2">
          <button
            @click="openFullscreen"
            class="p-1 rounded-sm bg-black/30 hover:bg-black/50 transition-colors"
            title="Fullscreen"
          >
            <FullScreenIcon class="w-6 h-6 text-white" />
          </button>
        </div>

        <!-- Center: tap empty area to reveal controls; play/pause only on the button -->
        <div
          class="flex-1 flex items-center justify-center"
          @click="onControlsActivity"
        >
          <button
            type="button"
            class="flex shrink-0 items-center justify-center rounded-full bg-black/50 w-20 h-20 outline-none focus:outline-none active:bg-black/50 [-webkit-tap-highlight-color:transparent]"
            :aria-label="isPlaying ? 'Pause' : 'Play'"
            @click.stop="togglePlayPause"
          >
            <PlayIcon v-if="!isPlaying" class="w-12 h-12 text-white ml-1 active:bg-transparent" />
            <PauseIcon v-else class="w-12 h-12 text-white active:bg-transparent" />
          </button>
        </div>

        <!-- Bottom bar: play button + time + scrubber -->
        <div class="bg-gradient-to-t from-black/70 to-transparent pt-6 px-3 pb-2.5">
          <div class="flex items-center gap-2">
            <button @click="togglePlayPause" class="flex-shrink-0">
              <PlayCircleIcon v-if="!isPlaying" class="w-6 h-6 text-white" />
              <PauseCircleIcon v-else class="w-6 h-6 text-white" />
            </button>

            <span class="flex-shrink-0 text-xs text-white/90 tabular-nums w-[35px]">{{ formatTime(currentTime) }}</span>

            <div class="flex-1">
              <div
                class="progress bg-white/40 rounded-full h-1.5 cursor-pointer"
                @click="handleProgressClick"
                @mousedown="handleMouseDown"
                @mousemove="handleMouseMove"
                @mouseup="handleMouseUp"
                @mouseleave="handleMouseLeave"
                @touchstart="handleTouchStart"
                @touchmove="handleTouchMove"
                @touchend="handleTouchEnd"
              >
                <div
                  class="progress-done h-1.5 rounded-full"
                  :style="{ width: `${progressPercentage}%`, backgroundColor: tourSettings?.page_action_color ?? '#fff' }"
                />
              </div>
            </div>

            <span class="flex-shrink-0 text-xs text-white/90 tabular-nums w-[35px] text-right">{{ formatTime(duration) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Fullscreen overlay -->
    <Teleport to="body">
      <div
        v-if="isFullscreen"
        class="fixed inset-0 z-50 bg-black"
        @mousemove="onControlsActivity"
        @touchstart.passive="onControlsActivity"
      >
        <!-- Video fills the entire screen -->
        <video
          ref="fullscreenVideoRef"
          class="w-full h-full object-contain"
          :src="mediaFile"
          playsinline
          @play="onVideoPlayEvent"
          @pause="onVideoPauseEvent"
          @timeupdate="handleTimeUpdate"
          @seeking="handleSeeking"
          @seeked="handleSeeked"
          @ended="onVideoEndedEvent"
        />

        <!-- Controls overlay (on top of the video) -->
        <div
          class="absolute inset-0 flex flex-col"
          :class="controlsOverlayClasses"
        >
          <!-- Top bar: close button -->
          <div class="flex justify-end p-4">
            <button
              @click="closeFullscreen"
              class="p-2 rounded-full bg-black/40 hover:bg-black/60 text-white transition-colors"
              aria-label="Close fullscreen"
            >
              <XMarkIcon class="w-6 h-6" />
            </button>
          </div>

          <!-- Center: tap empty area to reveal controls; play/pause only on the button -->
          <div
            class="flex-1 flex items-center justify-center"
            @click="onControlsActivity"
          >
            <button
              type="button"
              class="flex shrink-0 items-center justify-center rounded-full bg-black/50 w-20 h-20 outline-none focus:outline-none active:bg-black/50 [-webkit-tap-highlight-color:transparent]"
              :aria-label="isPlaying ? 'Pause' : 'Play'"
              @click.stop="togglePlayPause"
            >
              <PlayIcon v-if="!isPlaying" class="w-12 h-12 text-white ml-1 active:bg-transparent" />
              <PauseIcon v-else class="w-12 h-12 text-white active:bg-transparent" />
            </button>
          </div>

          <!-- Bottom bar: play button + time + scrubber -->
          <div class="bg-gradient-to-t from-black/80 to-transparent pt-10 px-5 pb-10">
            <div class="flex items-center gap-3">
              <button @click="togglePlayPause" class="flex-shrink-0">
                <PlayCircleIcon v-if="!isPlaying" class="w-7 h-7 text-white" />
                <PauseCircleIcon v-else class="w-7 h-7 text-white" />
              </button>

              <span class="flex-shrink-0 text-sm text-white/90 tabular-nums w-[40px]">{{ formatTime(currentTime) }}</span>

              <div class="flex-1">
                <div
                  class="progress bg-white/40 rounded-full h-1.5 cursor-pointer"
                  @click="handleProgressClick"
                  @mousedown="handleMouseDown"
                  @mousemove="handleMouseMove"
                  @mouseup="handleMouseUp"
                  @mouseleave="handleMouseLeave"
                  @touchstart="handleTouchStart"
                  @touchmove="handleTouchMove"
                  @touchend="handleTouchEnd"
                >
                  <div
                    class="progress-done h-1.5 rounded-full"
                    :style="{ width: `${progressPercentage}%`, backgroundColor: tourSettings?.page_action_color ?? '#fff' }"
                  />
                </div>
              </div>

              <span class="flex-shrink-0 text-sm text-white/90 tabular-nums w-[40px] text-right">{{ formatTime(duration) }}</span>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import FullScreenIcon from "@/vue/components/Icons/FullScreenIcon.vue";
import { PlayIcon, PauseIcon, PlayCircleIcon, PauseCircleIcon, XMarkIcon } from '@heroicons/vue/24/solid'

// Idle delay before hiding controls while playing
const CONTROLS_HIDE_DELAY_MS = 2000

export default {
  name: 'VideoBlock',
  components: {
    PlayIcon,
    PauseIcon,
    PlayCircleIcon,
    PauseCircleIcon,
    FullScreenIcon,
    XMarkIcon
  },
  props: {
    block: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      mediaFile: null,
      mimeType: null,
      thumbnailDataUrl: null,
      hasStartedPlaying: false,
      currentTime: 0,
      duration: 0,
      seekStartTime: null,
      playStartTime: null,
      totalPlayedTime: 0,
      seekDebounceTimeout: null,
      isPlaying: false,
      isDragging: false,
      isFullscreen: false,
      videoWidth: 0,
      videoHeight: 0,
      controlsVisible: true,
      controlsFading: false,
      controlsHideTimer: null
    }
  },
  computed: {
    controlsOverlayClasses() {
      return [
        this.isPlaying && !this.controlsVisible ? 'opacity-0' : 'opacity-100',
        this.controlsFading ? 'transition-opacity duration-300 ease-in-out' : ''
      ]
    },
    progressPercentage() {
      if (this.duration === 0) return 0
      return (this.currentTime / this.duration) * 100
    },
    currentLocale() {
      return this.$i18n?.locale || null
    },
    isReady() {
      return this.currentLocale && this.tourSettings
    },
    computedPoster() {
      return this.block?.data?.photo?.preview_url || this.thumbnailDataUrl || null
    },
    ...mapGetters('settings', ['tourSettings'])
  },
  watch: {
    block: {
      handler(newBlock) {
        if (!this.isReady) return
        if (newBlock?.data?.media && newBlock.data.media[this.currentLocale]) {
          const mediaData = newBlock.data.media[this.currentLocale]
          this.mediaFile = mediaData.preview_url
          // Default to video/mp4 when mime_type is not provided by the API
          this.mimeType = mediaData.mime_type || 'video/mp4'
        }
      },
      immediate: true
    }
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeydown)
  },
  beforeUnmount() {
    if (this.seekDebounceTimeout) {
      clearTimeout(this.seekDebounceTimeout)
    }
    this.clearControlsHideTimer()
    document.removeEventListener('keydown', this.handleKeydown)
  },
  methods: {
    getActiveVideo() {
      if (this.isFullscreen && this.$refs.fullscreenVideoRef) {
        return this.$refs.fullscreenVideoRef
      }
      return this.$refs.videoRef
    },

    togglePlayPause() {
      if (this.isPlaying) {
        this.handlePause()
      } else {
        this.handlePlay()
      }
    },

    onControlsActivity() {
      this.showControls()
    },

    showControls() {
      const wasHidden = this.isPlaying && !this.controlsVisible
      this.controlsVisible = true
      this.controlsFading = wasHidden
      this.clearControlsHideTimer()
      if (this.isPlaying && !this.isDragging) {
        this.scheduleHideControls()
      }
    },

    scheduleHideControls() {
      this.clearControlsHideTimer()
      if (!this.isPlaying || this.isDragging) return
      this.controlsHideTimer = setTimeout(() => {
        this.controlsFading = true
        requestAnimationFrame(() => {
          this.controlsVisible = false
          this.controlsHideTimer = null
        })
      }, CONTROLS_HIDE_DELAY_MS)
    },

    clearControlsHideTimer() {
      if (this.controlsHideTimer) {
        clearTimeout(this.controlsHideTimer)
        this.controlsHideTimer = null
      }
    },

    // --- User action methods: only call play/pause on the element ---

    handlePlay() {
      const video = this.getActiveVideo()
      if (video) {
        // Catch AbortError that occurs when pause() interrupts a pending play() promise
        video.play().catch(err => {
          if (err.name !== 'AbortError') console.warn('Video play error:', err)
        })
      }
    },

    handlePause() {
      const video = this.getActiveVideo()
      if (video) {
        video.pause()
      }
    },

    // --- DOM event handlers: only update state, never call play/pause ---

    onVideoPlayEvent() {
      this.playStartTime = Date.now()
      this.isPlaying = true
      this.hasStartedPlaying = true
      this.showControls()

      const videoName = this.block.data?.media?.[this.currentLocale]?.name || 'Untitled'
      if (window._paq) {
        window._paq.push(['trackEvent', 'Video', 'Play', videoName, this.currentTime])
      }
    },

    onVideoPauseEvent() {
      this.isPlaying = false
      this.controlsVisible = true
      this.controlsFading = false
      this.clearControlsHideTimer()

      if (this.playStartTime) {
        const sessionPlayTime = Math.floor((Date.now() - this.playStartTime) / 1000)
        const newTotalPlayedTime = this.totalPlayedTime + sessionPlayTime
        this.totalPlayedTime = newTotalPlayedTime
        this.playStartTime = null

        const videoName = this.block.data?.media?.[this.currentLocale]?.name || 'Untitled'
        if (window._paq) {
          window._paq.push(['setCustomDimension', 1, this.currentTime.toString()])
          window._paq.push(['setCustomDimension', 2, sessionPlayTime.toString()])
          window._paq.push(['setCustomDimension', 3, newTotalPlayedTime.toString()])
          window._paq.push(['trackEvent', 'Video', 'Pause', videoName, this.currentTime])
        }
      }
    },

    onVideoEndedEvent() {
      this.isPlaying = false
      this.controlsVisible = true
      this.controlsFading = false
      this.clearControlsHideTimer()
      this.currentTime = 0
      this.playStartTime = null
    },

    handleTimeUpdate() {
      const video = this.getActiveVideo()
      if (video) {
        this.currentTime = parseFloat(video.currentTime)
      }
    },

    handleSeeking() {
      if (this.playStartTime) {
        const sessionPlayTime = Math.floor((Date.now() - this.playStartTime) / 1000)
        this.totalPlayedTime = this.totalPlayedTime + sessionPlayTime
        this.playStartTime = null
      }
      if (this.seekStartTime === null) {
        this.seekStartTime = this.currentTime
        if (this.seekDebounceTimeout) {
          clearTimeout(this.seekDebounceTimeout)
          this.seekDebounceTimeout = null
        }
      }
    },

    handleSeeked() {
      if (this.seekStartTime !== null) {
        if (this.seekDebounceTimeout) {
          clearTimeout(this.seekDebounceTimeout)
        }
        this.seekDebounceTimeout = setTimeout(() => {
          const videoName = this.block.data?.media?.[this.currentLocale]?.name || 'Untitled'
          const fromTime = this.seekStartTime
          const toTime = this.getActiveVideo()?.currentTime ?? this.currentTime

          if (window._paq) {
            window._paq.push(['trackEvent', 'Video', 'Seek', videoName, toTime])
            window._paq.push(['setCustomDimension', 1, fromTime])
            window._paq.push(['setCustomDimension', 2, toTime])
          }

          this.seekStartTime = null
          this.seekDebounceTimeout = null

          if (!this.getActiveVideo()?.paused) {
            this.playStartTime = Date.now()
          }
        }, 500)
      }
    },

    openFullscreen() {
      this.showControls()
      const wasPlaying = this.isPlaying
      const resumeTime = this.currentTime
      // Pause inline video directly — onVideoPauseEvent will update isPlaying
      if (this.$refs.videoRef) {
        this.$refs.videoRef.pause()
      }
      this.isFullscreen = true

      this.$nextTick(() => {
        const fsVideo = this.$refs.fullscreenVideoRef
        if (!fsVideo) return

        const seekAndPlay = () => {
          fsVideo.currentTime = resumeTime
          if (wasPlaying) {
            fsVideo.play().catch(err => {
              if (err.name !== 'AbortError') console.warn('Fullscreen play error:', err)
            })
          }
        }

        if (fsVideo.readyState >= 1) {
          seekAndPlay()
        } else {
          fsVideo.addEventListener('loadedmetadata', seekAndPlay, { once: true })
        }
      })
    },

    closeFullscreen() {
      const wasPlaying = this.isPlaying
      if (this.$refs.fullscreenVideoRef) {
        this.currentTime = this.$refs.fullscreenVideoRef.currentTime
        // Pause fullscreen video directly
        this.$refs.fullscreenVideoRef.pause()
      }
      this.isFullscreen = false

      this.$nextTick(() => {
        if (this.$refs.videoRef) {
          this.$refs.videoRef.currentTime = this.currentTime
          if (wasPlaying) {
            this.$refs.videoRef.play().catch(err => {
              if (err.name !== 'AbortError') console.warn('Inline play error:', err)
            })
          }
        }
      })
    },

    handleKeydown(event) {
      if (event.key === 'Escape' && this.isFullscreen) {
        this.closeFullscreen()
      }
    },

    handleProgressClick(event) {
      this.showControls()
      const progressBar = event.currentTarget
      const rect = progressBar.getBoundingClientRect()
      const clickX = event.clientX - rect.left
      const clickPercentage = clickX / rect.width
      const newTime = clickPercentage * this.duration
      const video = this.getActiveVideo()
      if (video) {
        video.currentTime = newTime
        this.currentTime = newTime
      }
      this.handleSeeking()
      this.handleSeeked()
    },

    handleMouseDown(event) {
      this.showControls()
      this.isDragging = true
      this.handleDrag(event)
      event.preventDefault()
    },

    handleMouseMove(event) {
      if (this.isDragging) {
        this.handleDrag(event)
      }
    },

    handleMouseUp(event) {
      if (this.isDragging) {
        this.isDragging = false
        this.handleDrag(event)
        this.handleSeeking()
        this.handleSeeked()
        this.showControls()
      }
    },

    handleMouseLeave(event) {
      if (this.isDragging) {
        this.isDragging = false
        this.handleDrag(event)
        this.handleSeeking()
        this.handleSeeked()
      }
    },

    handleTouchStart(event) {
      this.showControls()
      this.isDragging = true
      this.handleTouchDrag(event)
      event.preventDefault()
    },

    handleTouchMove(event) {
      if (this.isDragging) {
        this.handleTouchDrag(event)
        event.preventDefault()
      }
    },

    handleTouchEnd() {
      if (this.isDragging) {
        this.isDragging = false
        this.handleSeeking()
        this.handleSeeked()
        this.showControls()
      }
    },

    handleDrag(event) {
      const progressBar = event.currentTarget
      const rect = progressBar.getBoundingClientRect()
      const clickX = event.clientX - rect.left
      const clickPercentage = Math.max(0, Math.min(1, clickX / rect.width))
      const newTime = clickPercentage * this.duration
      this.currentTime = newTime
      const video = this.getActiveVideo()
      if (video) {
        video.currentTime = newTime
      }
    },

    handleTouchDrag(event) {
      if (!event.touches || event.touches.length === 0) return
      const progressBar = event.currentTarget
      const rect = progressBar.getBoundingClientRect()
      const touch = event.touches[0]
      const clickX = touch.clientX - rect.left
      const clickPercentage = Math.max(0, Math.min(1, clickX / rect.width))
      const newTime = clickPercentage * this.duration
      this.currentTime = newTime
      const video = this.getActiveVideo()
      if (video) {
        video.currentTime = newTime
      }
    },

    onLoadedMetadata(event) {
      this.duration = event.target.duration
      this.videoWidth = event.target.videoWidth
      this.videoHeight = event.target.videoHeight

      // Capture first frame as fallback poster when the API provides no photo.
      // Uses a one-time seeked listener so it doesn't interfere with user seeks.
      if (!this.block?.data?.photo?.preview_url && !this.thumbnailDataUrl) {
        const video = event.target
        const captureFirstFrame = () => {
          video.removeEventListener('seeked', captureFirstFrame)
          const canvas = document.createElement('canvas')
          canvas.width = video.videoWidth || 640
          canvas.height = video.videoHeight || 360
          try {
            const ctx = canvas.getContext('2d')
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
            const dataUrl = canvas.toDataURL('image/jpeg', 0.85)
            // Discard empty/blank captures (data:, or very short strings)
            if (dataUrl && dataUrl !== 'data:,' && dataUrl.length > 500) {
              this.thumbnailDataUrl = dataUrl
            }
          } catch {
            // CORS restriction or other issue — silently ignore
          }
          video.currentTime = 0
        }
        video.addEventListener('seeked', captureFirstFrame)
        video.currentTime = 0.01
      }
    },

    formatTime(timeInSeconds) {
      if (!timeInSeconds || isNaN(timeInSeconds)) return '0:00'
      const minutes = Math.floor(timeInSeconds / 60)
      const seconds = Math.floor(timeInSeconds % 60)
      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    }
  }
}
</script>

<style scoped>
.progress {
  position: relative;
  overflow: hidden;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.progress-done {
  position: relative;
  transition: width 0.1s ease;
}

.progress:hover {
  cursor: grab;
}

.progress:active {
  cursor: grabbing;
}
</style>
